import React, { useCallback, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { DateTime } from 'luxon';
import { Stack, Button, Icon, P } from '@fashionnovateam/nova-ui-toolkit';
import {
  useGetTestingReports,
  useUploadTestingReport,
  validateFile,
} from '../../../Lib/Hooks/testing-reports.hooks';
import type { TestingReport } from '../../../Lib/Types/testing-report';
import { FileItem } from './FileItem';
import { DocumentCard } from './DocumentCard';

function formatTestingReportTooltip(report: TestingReport): string {
  const name = report.uploaded_by
    ? `${report.uploaded_by.first_name} ${report.uploaded_by.last_name}`.trim() ||
      'Unknown'
    : 'Unknown';
  const timestamp = report.created_at
    ? DateTime.fromISO(report.created_at).toFormat('MMM d, yyyy, h:mm a')
    : '';
  return timestamp
    ? `Uploaded by ${name} at ${timestamp}`
    : `Uploaded by ${name}`;
}

export type DocumentSectionProps = {
  canUpdate?: boolean;
  canDownload?: boolean;
  isUploaded: boolean;
  uploadProgress: number;
  isDisabled?: boolean;
  onUpload: (file: File) => void;
  onDownload: () => void;
  onRemove?: () => void;
};

type DocumentCenterProps = {
  userId: string;
  poNumber: string;
  disabled?: boolean;
  isCancelled?: boolean;
  invoice: DocumentSectionProps;
  packingList: DocumentSectionProps;
  canUploadTestingReports?: boolean;
};

export function DocumentCenter({
  userId,
  poNumber,
  disabled = false,
  isCancelled = false,
  invoice,
  packingList,
  canUploadTestingReports = false,
}: DocumentCenterProps): React.JSX.Element {
  const testingReportsInputRef = useRef<HTMLInputElement>(null);
  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const packingListInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const canUpload = canUploadTestingReports && !isCancelled;

  const { data: reports, isLoading: isLoadingReports } = useGetTestingReports(
    userId,
    poNumber,
    { enabled: canUploadTestingReports },
  );

  const { uploadTestingReport, isUploading, uploadProgress } =
    useUploadTestingReport();

  const handleTestingReportsUploadClick = useCallback(() => {
    setUploadError(null);
    testingReportsInputRef.current?.click();
  }, []);

  const handleTestingReportsFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (testingReportsInputRef.current) {
        testingReportsInputRef.current.value = '';
      }

      const validationError = validateFile(file);
      if (validationError) {
        setUploadError(validationError);
        return;
      }

      setUploadError(null);
      uploadTestingReport({ userId, poNumber, file });
    },
    [userId, poNumber, uploadTestingReport],
  );

  const handleInvoiceUploadClick = useCallback(() => {
    invoiceInputRef.current?.click();
  }, []);

  const handleInvoiceFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        invoice.onUpload(file);
      }
      if (invoiceInputRef.current) {
        invoiceInputRef.current.value = '';
      }
    },
    [invoice.onUpload],
  );

  const handlePackingListUploadClick = useCallback(() => {
    packingListInputRef.current?.click();
  }, []);

  const handlePackingListFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        packingList.onUpload(file);
      }
      if (packingListInputRef.current) {
        packingListInputRef.current.value = '';
      }
    },
    [packingList.onUpload],
  );

  const hasReports = reports && reports.length > 0;
  const showInvoiceCard = invoice.canUpdate || invoice.canDownload;
  const showPackingListCard = packingList.canUpdate || packingList.canDownload;

  return (
    <div className="mt-6 mb-6">
      <P size="xl" weight="medium" className="mb-4">
        Document Center
      </P>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {canUploadTestingReports && (
          <DocumentCard
            icon="upload/upload-up"
            title="Documents"
            description="Upload any additional files for this PO (e.g., lab testing, quality reports). Note: To maintain the audit trail, uploads cannot be removed."
            data-testid="document-card-testing-reports"
          >
            {canUpload && (
              <>
                <Button
                  size="sm"
                  color="secondary"
                  onClick={handleTestingReportsUploadClick}
                  disabled={disabled || isUploading}
                  className="inline-flex items-center gap-1.5"
                  data-testid="testing-reports-upload-btn"
                >
                  {isUploading ? (
                    <>
                      <Icon name="loading-circle" />
                      Uploading... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Icon name="paperclip/paperclip-slant" />
                      Attach File(s)
                    </>
                  )}
                </Button>
                <input
                  ref={testingReportsInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleTestingReportsFileChange}
                  className="hidden"
                  aria-label="Upload testing report"
                />
              </>
            )}

            {uploadError && (
              <div
                className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-md mt-3"
                data-testid="testing-reports-upload-error"
              >
                <Icon
                  name="alert/alert-circle"
                  className="text-red-500 flex-shrink-0"
                />
                <P size="xsm" color="error">
                  {uploadError}
                </P>
              </div>
            )}

            {hasReports && (
              <div className="mt-4">
                <P
                  size="2xs"
                  weight="medium"
                  color="disabled"
                  className="mb-2 uppercase tracking-wide"
                >
                  Uploaded Files
                </P>
                <Stack direction="col" gap="sm">
                  {reports.map((report: TestingReport) => (
                    <Tooltip
                      key={report.id}
                      title={formatTestingReportTooltip(report)}
                      enterDelay={1000}
                    >
                      <span className="block">
                        <FileItem
                          fileName={report.file_name}
                          onDownload={() => {
                            if (report.read_url) {
                              window.open(report.read_url, '_blank');
                            }
                          }}
                          downloadDisabled={!report.read_url}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
              </div>
            )}

            {isLoadingReports && (
              <div className="flex items-center gap-2 p-4">
                <Icon name="loading-circle" />
                <P size="xsm" color="disabled">
                  Loading...
                </P>
              </div>
            )}
          </DocumentCard>
        )}

        {showInvoiceCard && (
          <DocumentCard
            icon="file-02/file-02-text"
            title="Invoice"
            description="Upload the final commercial invoice for this PO. Ensure the invoice matches the PO quantity and pricing before submitting."
            data-testid="document-card-invoice"
          >
            {!invoice.isUploaded && invoice.canUpdate && (
              <>
                <Button
                  size="sm"
                  color="secondary"
                  onClick={handleInvoiceUploadClick}
                  disabled={disabled || invoice.uploadProgress > 0}
                  className="inline-flex items-center gap-1.5"
                >
                  {invoice.uploadProgress > 0 ? (
                    <>
                      <Icon name="loading-circle" />
                      Uploading... {Math.round(invoice.uploadProgress * 100)}%
                    </>
                  ) : (
                    <>
                      <Icon name="paperclip/paperclip-slant" />
                      Attach Invoice
                    </>
                  )}
                </Button>
                <input
                  ref={invoiceInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleInvoiceFileChange}
                  className="hidden"
                  aria-label="Upload invoice"
                />
              </>
            )}

            {invoice.isUploaded && (
              <FileItem
                fileName="Invoice.pdf"
                onDownload={invoice.onDownload}
                downloadDisabled={!invoice.canDownload}
                onRemove={invoice.canUpdate ? invoice.onRemove : undefined}
              />
            )}
          </DocumentCard>
        )}

        {showPackingListCard && (
          <DocumentCard
            icon="list/list-check"
            title="Packing List"
            description="Upload the packing list for this PO. If your list spans multiple POs, please combine them into a single PDF."
            data-testid="document-card-packing-list"
          >
            {!packingList.isUploaded &&
              packingList.canUpdate &&
              (packingList.isDisabled ? (
                <div className="flex items-start gap-2 p-3 bg-gray-100/50 rounded-md">
                  <Icon
                    size="16px"
                    variant="solid"
                    color="gray"
                    name="lock/lock-close"
                    className="flex-shrink-0 mt-0.5"
                  />
                  <P size="xsm" color="disabled">
                    You can upload the Packing List once the PO is scheduled for
                    Delivery.
                  </P>
                </div>
              ) : (
                <>
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={handlePackingListUploadClick}
                    disabled={disabled || packingList.uploadProgress > 0}
                    className="inline-flex items-center gap-1.5"
                  >
                    {packingList.uploadProgress > 0 ? (
                      <>
                        <Icon name="loading-circle" />
                        Uploading...{' '}
                        {Math.round(packingList.uploadProgress * 100)}%
                      </>
                    ) : (
                      <>
                        <Icon name="paperclip/paperclip-slant" />
                        Attach Packing List
                      </>
                    )}
                  </Button>
                  <input
                    ref={packingListInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePackingListFileChange}
                    className="hidden"
                    aria-label="Upload packing list"
                  />
                </>
              ))}

            {packingList.isUploaded && (
              <FileItem
                fileName="packing_list.pdf"
                onDownload={packingList.onDownload}
                downloadDisabled={!packingList.canDownload}
                onRemove={
                  packingList.canUpdate ? packingList.onRemove : undefined
                }
              />
            )}
          </DocumentCard>
        )}
      </div>
    </div>
  );
}
